import type { EpisodeOfCareModel } from '@ctw/shared/api/fhir/models/episode-of-care';
import type { OverviewCardDataProps } from '@ctw/shared/content/overview/resource-overview-card';
import { ResourceOverviewCard } from '@ctw/shared/content/overview/resource-overview-card';
import { ResourceTitleColumn } from '@ctw/shared/content/resource/resource-title-column';
import { tw } from '@ctw/shared/utils/tailwind';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons';
import { faCircleH } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { compact } from 'lodash-es';

const TITLE = 'ED & IP Visits';

export type EpisodeOfCareOverviewCardProps = OverviewCardDataProps<EpisodeOfCareModel>;

/**
 * Presentational ED & IP visits overview card. Renders the pre-sorted/filtered
 * list it is handed; data fetching lives in `useEpisodeOfCareOverviewData`.
 */
export const EpisodeOfCareOverviewCard = ({
  data,
  isLoading,
  emptyState,
  footerCTA,
  onRowClick,
}: EpisodeOfCareOverviewCardProps) => (
  <ResourceOverviewCard
    headerIcon={faCircleH}
    title={TITLE}
    data={data}
    emptyState={emptyState}
    onRowClick={onRowClick}
    footerCTA={footerCTA}
    loading={isLoading}
    helpText="Emergency and inpatient visits from the past year."
    testId="episodes-of-care-overview"
    renderResource={(episodeOfCare: EpisodeOfCareModel) => {
      const documents = episodeOfCare.dischargeBinaryIds;

      return (
        <ResourceTitleColumn
          badges={compact([
            episodeOfCare.isReadmission && {
              variant: 'info',
              content: 'Readmission',
            },
            episodeOfCare.isNonelectiveAdmission && {
              variant: 'info',
              content: 'Nonelective',
            },
            episodeOfCare.isBehavioralHealth && {
              variant: 'info',
              content: 'Behavioral',
            },
          ])}
          title={episodeOfCare.title}
          subTitle={
            <>
              <div className={tw`-mt-1 text-content-main/75 text-xs`}>{episodeOfCare.subtitle}</div>
              <div className={tw`space-y-0.5`}>
                {episodeOfCare.lastLocation && (
                  <div className={tw`font-normal`}>{episodeOfCare.lastLocation}</div>
                )}
                {documents.length > 0 ? (
                  <div className={tw`flex items-center space-x-1.5 pt-1 font-normal text-sm`}>
                    <FontAwesomeIcon className={tw`w-4 text-content-icon`} icon={faFileLines} />
                    <div>
                      {documents.length} {documents.length === 1 ? 'document' : 'documents'}
                    </div>
                  </div>
                ) : undefined}
              </div>
            </>
          }
        />
      );
    }}
  />
);
